import React, { useState, useEffect, useRef } from "react";
import { OaFieldProps } from "../../types/OaForm";
import ValidationMessage from "../forms/ValidationMessage";

/**
 * InputText props definitions.
 */
interface InputTextProps extends OaFieldProps<string> {
  className?: string;
  errorMessage?: string;
  type?: string;
  // Add new optional prop to enable filter mode
  filterMode?: boolean;
}

/**
 * Standardized OA Portal text input component.
 * Enhanced to prevent focus loss in filter mode while maintaining all existing functionality.
 * @param props
 * @returns
 */
export const InputText: React.FunctionComponent<InputTextProps> = ({
  type = "text",
  filterMode = false,
  ...props
}) => {
  /**
   * Managed state value for this input component.
   */
  const [value, setValue] = useState<string>(props.value ?? "");
  
  /**
   * For filter mode: track when to sync with parent to prevent focus loss
   */
  const isInitialRender = useRef(true);
  const lastPropValue = useRef<string>(props.value ?? "");

  useEffect(() => {
    let value = "";
    value = props?.value;

    // In filter mode, be more careful about when to update internal state
    if (filterMode) {
      // Only update if this is initial render or if parent value changed externally (like clear filter)
      if (isInitialRender.current || lastPropValue.current !== props.value) {
        setValue(value);
        lastPropValue.current = props.value ?? "";
        isInitialRender.current = false;
      }
    } else {
      // Original behavior for form mode
      setValue(value);
    }

    // Update the registered React Hook Form value.
    if (props.methods) {
      props.methods.setValue(props.name, value);
    }
  }, [props.value, filterMode]);

  useEffect(() => {
    if (props.methods && props.methods.formState.isDirty === false) {
      setValue(props.value ?? "");
    }
  }, [props.methods?.formState.isDirty]);

  /**
   * Get the CSS class for styling the form validity status.
   * @returns
   */
  const getClassName = () => {
    if (props.methods?.formState.errors[props.name]) {
      return `${props.className} is-invalid`;
    } else if (props.methods?.formState.dirtyFields[props.name]) {
      return `${props.className} is-valid`;
    }
    if (props.errorMessage) {
      return `${props.className} is-invalid`;
    }

    return props.className;
  };

  /**
   * On input change event handler.
   * Enhanced for filter mode to prevent unnecessary parent updates during typing.
   * @param e
   */
  const onInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const changedValue = e.target.value;
    
    if (type === "number") {
      if (e.target.value.match(/^[0-9]+$/)) {
        setValue(changedValue);
        // Update React Hook Form value
        if (props.methods) {
          props.methods.setValue(props.name, changedValue);
        }
        // In filter mode, only call onChange for valid number inputs
        if (!filterMode) {
          props.onChange && props.onChange(changedValue);
        }
      }
    } else {
      setValue(changedValue);
      // Update React Hook Form value
      if (props.methods) {
        props.methods.setValue(props.name, changedValue);
      }
      // In filter mode, don't call onChange during typing to prevent re-renders
      if (!filterMode) {
        props.onChange && props.onChange(changedValue);
      }
    }
  };

  /**
   * Handle blur event for filter mode - sync with parent when user finishes typing
   */
  const onInputBlur = () => {
    if (filterMode && value !== props.value) {
      props.onChange && props.onChange(value);
      lastPropValue.current = value;
    }
  };

  /**
   * Handle key events for filter mode - sync on Enter key
   */
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filterMode && e.key === 'Enter') {
      e.preventDefault();
      if (value !== props.value) {
        props.onChange && props.onChange(value);
        lastPropValue.current = value;
      }
    }
  };

  return (
    <React.Fragment>
      <input
        type={type}
        id={props.name}
        placeholder={props.placeholder}
        className={getClassName()}
        {...props.methods?.register(props.name)}
        onChange={onInputChange}
        onBlur={filterMode ? onInputBlur : undefined}
        onKeyDown={filterMode ? onKeyDown : undefined}
        value={value}
        disabled={props.disabled}
        readOnly={props.readonly}
      />

      <ValidationMessage
        message={props.methods?.formState.errors[props.name]?.message}
      />
      {props.errorMessage && (
        <p className="text-danger small">{props.errorMessage}</p>
      )}
    </React.Fragment>
  );
};

/**
 * Set the default InputText props.
 */
InputText.defaultProps = {
  className: "form-control oa-input",
};